/**
 * PATH: packages/email-hub/src/feature/components/DspyArtifacts.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-DSPYARTIFACTS-001
 * PURPOSE: Collapsible diagnostics card listing the compiled DSPy
 *          email-classifier artifacts (version, status, eval score) with
 *          promote / rollback controls. Mounted in the diagnostics drawer
 *          next to the shadow-resolution card.
 * WHY:     Phase 3 ships the learning-loop artifact + lifecycle dark. This
 *          is the operator control: see what the nightly compile produced
 *          and promote the best candidate to 'active' (or roll the active
 *          one back to fall straight back to the legacy classifier).
 *          Hidden when there are no artifacts, so it adds nothing until the
 *          compile job has run.
 * RELATED:
 *   - _internal/services/dspyArtifacts.ts: the data source + actions.
 *   - backend organisation/services/email_dspy_admin.py: payload + lifecycle.
 */

import { useState } from 'react'
import { Brain, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import {
  useDspyArtifacts,
  type DspyArtifact,
} from '../../_internal/services/dspyArtifacts'

/** Pure: short label for an artifact row. Exported for unit testing. */
export function artifactLabel(a: DspyArtifact): string {
  const score = a.eval_score != null ? ` · score ${(a.eval_score * 100).toFixed(0)}%` : ''
  return `v${a.version_number} · ${a.scope}${score}`
}

/** Pure: Tailwind tone classes for a lifecycle status. Exported for tests. */
export function statusTone(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    case 'candidate':
    case 'canary':
      return 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
    case 'rolled_back':
      return 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
    case 'archived':
      return 'bg-foreground/10 text-muted-foreground'
    default: // draft
      return 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
  }
}

function Row({
  a,
  onPromote,
  onRollback,
}: {
  a: DspyArtifact
  onPromote: (id: string) => void
  onRollback: (id: string) => void
}) {
  const canPromote = a.status !== 'active'
  return (
    <div className="flex items-center gap-2 py-1 border-b border-foreground/5 last:border-0">
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${statusTone(a.status)}`}>
        {a.status}
      </span>
      <span className="text-[11px] truncate">{artifactLabel(a)}</span>
      <span className="ml-auto flex gap-1">
        {canPromote && (
          <button
            type="button"
            onClick={() => onPromote(a.id)}
            className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Promote
          </button>
        )}
        {a.status === 'active' && (
          <button
            type="button"
            onClick={() => onRollback(a.id)}
            className="text-[10px] px-1.5 py-0.5 rounded bg-rose-600 text-white hover:bg-rose-700"
          >
            Rollback
          </button>
        )}
      </span>
    </div>
  )
}

export default function DspyArtifacts({ open }: { open: boolean }) {
  const { data, loading, available, error, promote, rollback } = useDspyArtifacts(open)
  const [expanded, setExpanded] = useState(false)

  const artifacts = data?.artifacts ?? []
  const hasData = available && artifacts.length > 0
  if (!hasData) {
    if (loading) {
      return (
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground px-1 py-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading AI model artifacts…
        </div>
      )
    }
    return null
  }

  const active = artifacts.find(a => a.is_active)
  const verdict = active
    ? `active v${active.version_number}`
    : `${artifacts.length} artifact${artifacts.length > 1 ? 's' : ''} · none active`

  const handlePromote = (id: string) => {
    if (window.confirm('Promote this artifact to ACTIVE? It will serve production email classification.')) {
      void promote(id)
    }
  }
  const handleRollback = (id: string) => {
    const reason = window.prompt('Rollback the active artifact (falls back to the legacy classifier). Reason:')
    if (reason !== null) void rollback(id, reason)
  }

  return (
    <div className="border border-sky-300/40 dark:border-sky-700/40 bg-sky-500/5 rounded-md p-2.5">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-2 text-left"
      >
        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <Brain className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
        <span className="text-xs font-semibold">AI classifier models</span>
        <span className="ml-auto text-[11px] text-muted-foreground truncate max-w-[55%]">{verdict}</span>
      </button>

      {expanded && (
        <div className="mt-2.5 space-y-1">
          {artifacts.map(a => (
            <Row key={a.id} a={a} onPromote={handlePromote} onRollback={handleRollback} />
          ))}
          <p className="text-[10px] text-muted-foreground pt-1">
            {data?.dspy_runtime_enabled
              ? 'DSPy runtime is ON - the active model serves classification.'
              : 'DSPy runtime is OFF - promoting takes effect once the EMAIL_AI_DSPY flag is enabled.'}
          </p>
          {error && <p className="text-[10px] text-destructive">{error}</p>}
        </div>
      )}
    </div>
  )
}
